/**
 * AI Chatbot Types
 *
 * TypeScript type definitions for the AI Chatbot feature.
 * Phase 7: Frontend Integration
 *
 * @module ChatbotTypes
 */

// DRY: Import shared types from @axon/common (single source of truth)
import type { SpotlightStep, SpotlightData } from '@axon/common';
export type { SpotlightStep, SpotlightData };

// ============================================================================
// Message Types
// ============================================================================

/**
 * Message role enumeration
 */
export type MessageRole = 'user' | 'assistant' | 'system';

/**
 * Message content type enumeration
 */
export type MessageType =
  | 'text'
  | 'data_query'
  | 'report'
  | 'error'
  | 'suggestion'
  | 'action_confirmation'
  | 'action_result'
  | 'proactive'
  | 'navigation'
  | 'workflow';

/**
 * Feedback rating options
 */
export type FeedbackRating = 'helpful' | 'not_helpful';

/**
 * Chart data type for visualizations
 */
export interface ChartData {
  type: 'bar' | 'line' | 'pie' | 'table';
  title: string;
  data: Record<string, unknown>[];
  xAxis?: string;
  yAxis?: string;
}

/**
 * Message feedback structure
 */
export interface MessageFeedback {
  rating?: FeedbackRating;
  comment?: string;
}

/**
 * Structured table data for data query results (Phase 16)
 */
export interface TableData {
  headers: string[];
  rows: string[][];
}

/**
 * Message metadata structure
 */
export interface MessageMetadata {
  intent?: string;
  /** Backend message id (from RESPONSE_COMPLETE) — used to target enrichment merges. */
  serverMessageId?: string;
  sources?: string[];
  chartData?: ChartData;
  followUpSuggestions?: string[];
  processingTimeMs?: number;
  feedback?: MessageFeedback;
  originalQuery?: string;
  /** Data insight from enrichment (Phase 16) */
  insight?: string;
  /** Proactive suggestion from enrichment (Phase 16) */
  proactiveTip?: string;
  /** Structured table data for frontend rendering (Phase 16) */
  tableData?: TableData;
  /** Clarification options when confidence is uncertain (Phase 16) */
  clarificationOptions?: string[];
  /** Action confirmation data (Phase 18) */
  actionConfirmation?: ActionConfirmationData;
  /** Action execution result data (Phase 18) */
  actionResult?: ActionResultData;
  /** Navigation data (Phase 19) */
  navigation?: NavigationData;
  /** Spotlight/guided tour data (Phase 19E) */
  spotlight?: SpotlightData;
  /** Workflow execution result data (Phase 19F) */
  workflow?: WorkflowData;
}

/**
 * Navigation data embedded in message metadata (Phase 19)
 */
export interface NavigationData {
  path: string;
  entityId?: string | null;
  label?: string;
  /** Form pre-fill data for create actions (Phase 19D) */
  formPrefill?: Record<string, string> | null;
}

// SpotlightStep and SpotlightData are now re-exported from @axon/common above (DRY)

/**
 * Workflow step data for frontend rendering (Phase 19F)
 */
export interface WorkflowStepData {
  index: number;
  actionType: string;
  entityType: string;
  entityId: string;
  status: 'pending' | 'running' | 'success' | 'failed' | 'cancelled';
  resultMessage?: string;
}

/**
 * Workflow execution result data (Phase 19F)
 */
export interface WorkflowData {
  success: boolean;
  totalSteps: number;
  completedSteps: number;
  steps: WorkflowStepData[];
  stepDescriptions?: string[];
}

/**
 * Action confirmation data embedded in message metadata (Phase 18)
 */
export interface ActionConfirmationData {
  confirmationId: string;
  actionType: string;
  entityType: string;
  entityId: string;
  safetyLevel: string;
  requiresReason: boolean;
}

/**
 * Action execution result data embedded in message metadata (Phase 18)
 */
export interface ActionResultData {
  success: boolean;
  actionType: string;
  entityType: string;
  entityId: string;
  changes?: Record<string, { from: unknown; to: unknown }>;
}

/**
 * Proactive suggestion pushed from server (Phase 18)
 */
export interface ProactiveSuggestion {
  id: string;
  type: string;
  message: string;
  suggestedAction?: {
    actionType: string;
    entityType: string;
    entityId: string;
    label: string;
  };
  priority: 'low' | 'medium' | 'high' | 'critical';
  createdAt: string;
  data?: Record<string, unknown>;
}

/**
 * Chat message structure
 */
export interface ChatMessage {
  id: string;
  conversationId: string;
  role: MessageRole;
  content: string;
  type: MessageType;
  metadata?: MessageMetadata;
  createdAt: Date;
  isStreaming?: boolean;
}

// ============================================================================
// Session Types
// ============================================================================

/**
 * Supported languages
 */
export type ChatLanguage = 'en' | 'tr';

/**
 * Chat session structure
 */
export interface ChatSession {
  conversationId: string;
  isConnected: boolean;
  isTyping: boolean;
  language: ChatLanguage;
}

// ============================================================================
// State Types
// ============================================================================

/**
 * Chatbot state structure
 */
export interface ChatbotState {
  isOpen: boolean;
  isMinimized: boolean;
  session: ChatSession | null;
  messages: ChatMessage[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  /** Proactive suggestions from server (Phase 18) */
  proactiveSuggestions: ProactiveSuggestion[];
}

/**
 * Chatbot context value (state + actions)
 */
export interface ChatbotContextValue extends ChatbotState {
  /** Live socket connection state (distinct from session.isConnected). */
  isConnected: boolean;
  /** True while socket.io is attempting/retrying a connection. */
  isConnecting: boolean;
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  minimizeChat: () => void;
  sendMessage: (content: string) => Promise<void>;
  sendFeedback: (messageId: string, rating: FeedbackRating, comment?: string) => void;
  clearMessages: () => void;
  reconnect: () => Promise<void>;
  /** Confirm or cancel a pending action (Phase 18) */
  confirmAction: (confirmationId: string, confirmed: boolean, reason?: string) => void;
  /** Dismiss a proactive suggestion (Phase 18) */
  dismissSuggestion: (suggestionId: string) => void;
  /**
   * Start a guided tour / spotlight walkthrough from anywhere in the app.
   * Reuses the globally-mounted spotlight engine (Phase 19E) so callers
   * (e.g. the System Flow guided-demo launcher) don't instantiate a second
   * overlay. Steps may carry an empty `spotlightId` for center-screen narration.
   */
  startGuidedTour: (steps: SpotlightStep[]) => void;
  /** Stop the active guided tour / spotlight walkthrough. */
  stopGuidedTour: () => void;
}

// ============================================================================
// Action Types (for useReducer)
// ============================================================================

/**
 * Chatbot reducer actions
 */
export type ChatbotAction =
  | { type: 'OPEN_CHAT' }
  | { type: 'CLOSE_CHAT' }
  | { type: 'TOGGLE_CHAT' }
  | { type: 'MINIMIZE_CHAT' }
  | { type: 'SET_SESSION'; payload: ChatSession }
  | { type: 'CLEAR_SESSION' }
  | { type: 'ADD_MESSAGE'; payload: ChatMessage }
  | { type: 'UPDATE_MESSAGE'; payload: { id: string; updates: Partial<ChatMessage> } }
  | { type: 'APPEND_CHUNK'; payload: { chunk: string } }
  | { type: 'SET_TYPING'; payload: boolean }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_MESSAGES' }
  | { type: 'MARK_MESSAGES_READ' }
  | { type: 'ADD_PROACTIVE_SUGGESTION'; payload: ProactiveSuggestion }
  | { type: 'DISMISS_PROACTIVE_SUGGESTION'; payload: string };

// ============================================================================
// WebSocket Event Types
// ============================================================================

/**
 * WebSocket connection data
 */
export interface SocketConnectData {
  conversationId: string;
  language: ChatLanguage;
}

/**
 * WebSocket error structure
 */
export interface SocketError {
  code: string;
  message: string;
}

/**
 * WebSocket chat response data
 */
export interface SocketChatResponse {
  message: ChatMessage;
}

/**
 * WebSocket chunk data
 */
export interface SocketChunkData {
  chunk: string;
}

/**
 * WebSocket typing data
 */
export interface SocketTypingData {
  isTyping: boolean;
}

// ============================================================================
// Hook Options Types
// ============================================================================

/**
 * useChatSocket hook options
 */
/**
 * Stream complete event data from server
 */
export interface StreamCompleteData {
  conversationId?: string;
  /** Backend message id — anchors post-hoc `chat:message_enriched` merges. */
  messageId?: string;
  type?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Payload of `chat:message_enriched` — post-hoc LLM enrichment
 * (follow-up suggestions, data insight) for an already-delivered
 * message. The response ships immediately with static fallbacks;
 * this lands seconds later and is merged into the message metadata.
 */
export interface MessageEnrichedData {
  messageId: string;
  metadata: Record<string, unknown>;
}

/**
 * useChatSocket hook options
 */
export interface UseChatSocketOptions {
  onConnect?: (data: SocketConnectData) => void;
  onMessage?: (message: ChatMessage) => void;
  onChunk?: (chunk: string) => void;
  onTyping?: (isTyping: boolean) => void;
  onError?: (error: SocketError) => void;
  onStreamComplete?: (data: StreamCompleteData) => void;
  onDisconnect?: () => void;
  /** Action confirmation request from server (Phase 18) */
  onActionConfirm?: (
    data: ActionConfirmationData & {
      description: string;
      currentState?: Record<string, unknown>;
      conversationId: string;
    }
  ) => void;
  /** Action execution result from server (Phase 18) */
  onActionResult?: (data: ActionResultData & { message: string; conversationId: string }) => void;
  /** Proactive suggestion from server (Phase 18) */
  onProactiveSuggestion?: (suggestion: ProactiveSuggestion) => void;
  /** Navigation command from server (Phase 19) */
  onNavigate?: (data: NavigationData) => void;
  /** Workflow completion from server (Phase 19F) */
  onWorkflowComplete?: (data: Record<string, unknown>) => void;
  /** Post-hoc enrichment for a delivered message */
  onMessageEnriched?: (data: MessageEnrichedData) => void;
}

// ============================================================================
// API Types
// ============================================================================

/**
 * Chat request payload
 */
export interface ChatRequest {
  content: string;
  conversationId?: string;
  language?: ChatLanguage;
}

/**
 * Feedback request payload
 */
export interface FeedbackRequest {
  messageId: string;
  rating: FeedbackRating;
  comment?: string;
}

// ============================================================================
// Suggestion Types
// ============================================================================

/**
 * Quick suggestion chip
 */
export interface SuggestionChip {
  id: string;
  label: string;
  query: string;
}

// ============================================================================
// Chat Window Size (Phase 16: Expandable Window)
// ============================================================================

/**
 * Chat window size mode
 */
export type ChatWindowSize = 'default' | 'expanded';

/**
 * Chat window dimension constants (DRY: no magic numbers in components)
 */
export const CHAT_WINDOW_SIZES: Record<ChatWindowSize, { width: number; height: number }> = {
  default: { width: 380, height: 560 },
  expanded: { width: 600, height: 700 },
};
